import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Grid, Paper, Typography } from '@material-ui/core'
import i18next from 'i18next'
import moment from 'moment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumFormaPago } from '../../../api/Argentia/enums/enumFormaPago'
import {
  ActiveContractModelResponse,
  ActivityTypeModel,
  ContractModel,
  PaymentMethodTypeModel
} from '../../../api/Argentia/model/contracts'
import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import Page, { PageLayout } from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { BreadcrumbLink } from '../../../components/shared/breadcrumbLink/breadcrumbLink'
import {
  DATE_FORMATS,
  DEFAULT_DATE_FORMAT
} from '../../../components/shared/priceExpressionValidator/components/dateInput'
import { hasPermission, hasResource } from '../../../helpers/permissionsHelper'
import { missingFieldsForm } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog, useHttpError } from '../../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import { Resources } from '../../../model/RBAC/constants'
import { fetchClientContractsByID, getCustomerDomain, notifyError } from '../../../redux'
import { selectClientData } from '../../../redux/selectors/client.selectors'
import { getClientName, getLinkClientText } from '../../clients/client.helper'
import { fetchClient } from '../../clients/client.service'
import { SubjectModel } from '../../clients/clients.model'
import {
  listCnaeItemMapper,
  listTollItemMapper,
  mapATRVersionToTableModel,
  paymentMethodTypesToOptionsMapper
} from '../contracts.mapper'
import {
  ContractsFormValidations,
  ContractSuppliesFormValidations,
  RulebookContractsValidations
} from '../contracts.model'
import {
  changeOwner,
  checkValidIBAN,
  fetchActivityTypes,
  fetchContractsByCups,
  fetchPaymentMethodsTypes,
  fetchPowerPeriodsByToll,
  fetchTolls,
  getContractDetails
} from '../contracts.service'
import CupsFilter, { CHANGE_TYPE } from './components/cupsFilter'
import { EnumCommodity } from '../../../api/Argentia/enums'
import { ProcedureFormMode } from '../../procedures/procedures.types'
import { enumEnumToValueNumber, enumKeyToValue, enumToSelectOptionArray } from '../../../helpers'
import { fetchQuotes } from '../../quotes/quotes.service'
import { fetchProductsBySubjectType } from '../../products/products.service'
import { EnumServicio, EnumTipoProductoCapacidad, EnumTipoSujeto } from '../../../api'
import { QuotesModel } from '../../../api/Argentia/endpoints/quotes/model/quotes'
import { EnumTipoPeaje } from '../../../api/Argentia/enums/enumTipoPeaje'
import { VALID_TOLLTYPE } from '../../procedures/procedures.helper'
import { EnumTipoContratoATR } from '../../../api/oData/enums/enumTipoContratoATR'
interface ContractChangeOwnerPage {
  location: {
    state: {
      contractID: number
      contract: ContractModel
      initialCups?: string
      initialStartDate?: Date
      initialEndDate?: Date
    }
  }
}

const initialValidations: ContractSuppliesFormValidations = {
  isDirty: false,
  startDate: true,
  endDate: true,
  signDate: true,
  contractCode: true,
  iban: true,
  postalCode: true,
  cupsCode: true,
  newStartDate: true,
  newEndDate: true,
  startDateContract: true,
  endDateContract: true,
  newStartDateContract: true,
  newEndDateContract: true,
  newStartDatePast: true
}

export interface ChangeOwnerForm {
  cups: string
  newStartDate: Date
  newEndDate: Date
  newClientId: number
  newClientDesc: string
  activeStartDate: Date | null
  activeEndDate: Date | null
  activeClientId: number | null
  activeClientDesc: string | null
  activeContractId: number | null
  activeContractDesc: string | null
  activeQuoteId: number | null
  activeProductId: number | null
  activeProductCode: string | null
  signDate: Date
  saleDate: Date
  paymentMethodId: number
  iban: string
  changeType?: string
  type?: number
  toll?: number
  qaEstimada?: number
  qdContratada?: number
  contractType?: number
  state?: number
  cnae?: string
  commodity: EnumCommodity
  tollType?: number | null
  period1?: number | null
  period2?: number | null
  period3?: number | null
  period4?: number | null
  period5?: number | null
  period6?: number | null
}

interface PeriodsFormConfig {
  1: boolean
  2: boolean
  3: boolean
  4: boolean
  5: boolean
  6: boolean
}

const initialPeriodsFormConfig = {
  1: false,
  2: false,
  3: false,
  4: false,
  5: false,
  6: false
}

interface initialModal
  extends Pick<ChangeOwnerForm, 'cups' | 'newStartDate' | 'newEndDate' | 'changeType'> {}

export const ContractChangeOwnerPage: React.FC<ContractChangeOwnerPage> = ({ location = null }) => {
  const history = useHistory()
  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { info } = useNotify()
  const { errorProps } = useHttpError()
  const { updateFormDefinition, setFormWaiting, updateFormData } = useFormDialog()

  const [t] = useTranslation(['contracts', 'globalerisLibrary', 'enums'])

  const clientData = useSelector(selectClientData)

  const [formData, setFormData] = useState<Partial<ChangeOwnerForm>>({})
  const [activeContract, setActiveContract] = useState<ActiveContractModelResponse | null>(null)
  const [activeSubjectData, setActiveSubject] = useState<SubjectModel | null>(null)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [pendingChanges, setPendingChanges] = useState<boolean>(false)
  const [validations, setValidations] = useState<ContractsFormValidations>(initialValidations)

  const [ibanRequired, setIbanRequired] = useState<boolean>(false)
  const [cupsNotFound, setCupsNotFound] = useState<boolean>(false)
  const [activeContractNotFound, setActiveContractNotFound] = useState<boolean>(false)
  const [multipleContractsFound, setMultipleContractsFound] = useState<boolean>(false)

  const [productTypeOptions, setProductTypeOptions] = useState<any[] | undefined>([])
  const [quoteOptions, setQuoteOptions] = useState<SelectOption[] | undefined>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodTypeModel[]>([])
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<SelectOption[]>([])
  const [isFetchingPaymentOptions, setIsFetchingPaymentOptions] = useState<boolean>(false)
  const [showCapacitySection, setShowCapacitySection] = useState<boolean>(false)
  const [showAtrSection, setShowAtrSection] = useState<boolean>(false)
  const [tollSelectOptionList, setTollSelectOptionList] = useState<SelectOption[]>([])
  const [cnaeList, setCnaeList] = useState<ActivityTypeModel[]>([])
  const [isCnaeRequired, setIsCnaeRequired] = useState<boolean>(false)
  const cnaeListRef = useRef<ActivityTypeModel[]>([])
  const [periodsVersionFormConfig, setPeriodsVersionFormConfig] =
    useState<PeriodsFormConfig>(initialPeriodsFormConfig)

  useEffect(() => {
    cnaeListRef.current = cnaeList
  }, [cnaeList])

  const formOptions: FormOptions = useMemo(() => {
    return {
      columns: 3,
      analytics: {
        customer: getCustomerDomain(),
        view: t('contracts:forms.changeOwner.analytics')
      }
    }
  }, [t, getCustomerDomain])

  const handleFormDirty = (
    validation: RulebookContractsValidations,
    valid: boolean,
    cause?: string
  ) => {
    setValidations(prevValidations => {
      const newValidation = { ...prevValidations, [validation]: valid }
      const isDirty = !(
        newValidation.startDate &&
        newValidation.endDate &&
        newValidation.signDate &&
        newValidation.contractCode &&
        newValidation.iban
      )
      return { ...newValidation, isDirty: isDirty }
    })
  }

  const handlePaymentMethodChange = (paymentMethodId: number) => {
    const paymentMethod = paymentMethods.find(paymentMethod => paymentMethod.Id === paymentMethodId)

    if (paymentMethod && paymentMethod.methodType === EnumFormaPago.Domiciliacion) {
      setIbanRequired(true)
    } else {
      ibanRequired && setIbanRequired(false)
    }
  }

  const getQuotes = async (activeProductId: number) => {
    await fetchQuotes(activeProductId).then(response => {
      if (response.data !== undefined) {
        setQuoteOptions(mapQuoteModelListToSelectOption(response.data))
      }
    })
  }

  const getTolls = async (commodity: EnumCommodity) => {
    await fetchTolls(commodity).then(response => {
      if (response.data) {
        setTollSelectOptionList(listTollItemMapper(response.data))
      }
    })
  }

  const onChangeToll = async (toll: any) => {
    if (toll) {
      await fetchPowerPeriodsByToll(toll).then(response => {
        if (response.data !== undefined) {
          const periodsQty: number = response.data
          const config = getPeriodsConfig(periodsQty)
          setPeriodsVersionFormConfig(config as PeriodsFormConfig)
          updatePeriodsValues(config as PeriodsFormConfig)
        }
      })
    }
  }

  const updatePeriodsValues = (showPeriod: PeriodsFormConfig) => {
    let periods = {}
    for (let i = 1; i <= 6; i++) {
      if (!showPeriod[i]) {
        // If the period is not valid for this Toll, set its value to undefined
        periods[`period${i}`] = undefined
      }
    }
    updateFormData(prevState => ({ ...prevState, ...periods }))
  }

  const getPeriodsConfig = (periodsQtyToShow: number) => {
    let config = {}
    for (let i = 1; i <= 6; i++) {
      config[i] = i <= periodsQtyToShow ? true : false
    }
    return config
  }

  const mapQuoteModelListToSelectOption = (quotes: any): SelectOption[] => {
    return quotes
      .map(quote => quoteMapper(quote))
      .filter((option: SelectOption | null) => option !== null)
  }

  const quoteMapper = (model: QuotesModel): SelectOption => {
    return (
      model && {
        value: model.id,
        label: model.code
      }
    )
  }

  const getProductTypeOptions = async (commodity: EnumCommodity) => {
    await fetchProductsBySubjectType(EnumTipoSujeto.ClienteFinal, commodity).then(response => {
      if (response.data !== undefined) {
        setProductTypeOptions(response.data)
      }
    })
  }

  const handleNewIban = async (iban: string) => {
    const response = await checkValidIBAN(iban)
    if (response.success && response.data) {
      if (response.data.isValid) {
        handleFormDirty('iban', true)
      } else {
        handleFormDirty('iban', false)
      }
    }
  }

  const validateStartDate = (startDate?: Date, endDate?: Date | null) => {
    if (endDate && startDate && moment(endDate).isBefore(moment(startDate), 'days')) {
      handleFormDirty(RulebookContractsValidations.START_DATE, false)
    } else {
      handleFormDirty(RulebookContractsValidations.START_DATE, true)
      handleFormDirty(RulebookContractsValidations.END_DATE, true)
    }
  }

  const validateEndDate = (startDate?: Date, endDate?: Date | null) => {
    if (startDate && endDate && moment(endDate).isBefore(moment(startDate), 'days')) {
      handleFormDirty(RulebookContractsValidations.END_DATE, false)
    } else {
      handleFormDirty(RulebookContractsValidations.START_DATE, true)
      handleFormDirty(RulebookContractsValidations.END_DATE, true)
    }
  }

  const queryContractByCUPSDates = async (
    cups: string,
    startDate: Date,
    endDate: Date
  ): Promise<boolean> => {
    let contractExists = false
    if (startDate && endDate && cups) {
      const response = await fetchContractsByCups(cups, 1, startDate, endDate)
      if (response.success) {
        if (response.data && response.data.length === 1) {
          activeContractNotFound && setActiveContractNotFound(false)
          multipleContractsFound && setMultipleContractsFound(false)
          contractExists = true
          const cupsContract = response.data[0]

          setActiveContract(cupsContract)
          setFormData(prevState => ({
            ...prevState,
            cups: cups,
            newClientId: clientData?.id,
            newClientDesc: getClientName(clientData),
            activeContractId: cupsContract.id,
            activeContractDesc: cupsContract.contractCode,
            activeContractStartDate: cupsContract.startDate,
            activeContractEndDate: cupsContract.endDate,
            newStartDate: moment(startDate).toDate(),
            newEndDate: moment(endDate).toDate(),
            signDate: moment().toDate(),
            saleDate: moment().toDate()
          }))
          if (cupsContract.subjectID) {
            const responseSubject = await fetchClient(cupsContract.subjectID)
            if (responseSubject.success && responseSubject.data?.id) {
              setActiveSubject(responseSubject.data)
              setFormData(prevState => ({
                ...prevState,
                activeClientId: cupsContract.subjectID,
                activeClientDesc: responseSubject.data?.description
              }))
            } else {
              // subject not found or api error
              notifyError('Error interno al intentar buscar el cliente.')
            }
          }
        } else {
          if (response.data && response.data.length > 0) {
            setMultipleContractsFound(true)
          } else {
            setActiveContractNotFound(true)
          }

          setFormData(prevState => ({
            ...prevState,
            newStartDate: undefined,
            newEndDate: undefined,
            activeClientDesc: null,
            activeClientId: null,
            activeContractDesc: null,
            activeContractId: null,
            activeStartDate: null,
            activeEndDate: null
          }))
        }
      } else {
        notifyError('Error interno al intentar buscar el contrato.')
      }
    }
    return contractExists
  }

  const onSave = async () => {
    if (!isSaving) {
      if (missingFieldsForm(formSection, formData)) {
        notifyError(t('contracts:notifications.create.errorInvalidForm'))
      } else {
        setIsSaving(true)
        clientData?.id &&
          activeContract &&
          (await changeOwner(formData as ChangeOwnerForm, activeContract, clientData.id).then(
            response => {
              if (response.success) {
                info(t('contracts:notifications.create.success'))
                // If user has Permission, ask if he wants to go to procedures add page
                if (formData.changeType === CHANGE_TYPE.CHANGE_OWNER_ONLY) {
                  history.push(NonAuthRoutes.ClientFile, { clientID: clientData?.id })
                  fetchClientContractsByID(clientData?.id!)
                  return true
                }
                if (hasResource(Resources.ComunicationsCenterProcedures)) {
                  let procedureFormMode: string

                  if (
                    enumKeyToValue(EnumCommodity, activeContract.commodity) ===
                    EnumCommodity.GasNatural
                  ) {
                    procedureFormMode =
                      formData.changeType === CHANGE_TYPE.ADM_TEC_CHANGE
                        ? ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_GAS
                        : ProcedureFormMode.CAMBIO_TITULAR_GAS
                  } else {
                    procedureFormMode =
                      formData.changeType === CHANGE_TYPE.ADM_TEC_CHANGE
                        ? ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_POWER
                        : ProcedureFormMode.CAMBIO_TITULAR_POWER
                  }

                  history.push(NonAuthRoutes.ProceduresAdd, {
                    contractId: response.data!.id,
                    oldContractId: activeContract.id,
                    formMode: procedureFormMode,
                    newDate: formData.newStartDate
                  })
                } else {
                  history.goBack()
                }
              } else {
                notifyError(t('contracts:notifications.edition.error'))
              }
              setIsSaving(false)
            }
          ))
      }
    }
  }

  const initialModalDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'initialModal',
        sections: [
          {
            inputs: [
              {
                name: 'cups',
                label: t('contracts:forms.changeOwner.inputs.cups'),
                type: DataType.TEXT,
                required: true,
                errorState: cupsNotFound || activeContractNotFound || multipleContractsFound,
                errorText: cupsNotFound
                  ? 'No existe el CUPS seleccionado en el sistema, dar de alta como contrato normal.'
                  : activeContractNotFound
                    ? 'El CUPS seleccionado no tiene un contrato activo asociado.'
                    : multipleContractsFound
                      ? 'Existen múltiples contratos activos para el período seleccionado. Seleccione un período menor.'
                      : '',

                onChange: (value, formState) => {
                  const state = formState as initialModal
                  if (
                    value &&
                    moment(state?.newStartDate).isValid() &&
                    moment(state?.newEndDate).isValid()
                  ) {
                    setFormWaiting(true)
                    queryContractByCUPSDates(`${value}`, state.newStartDate, state.newEndDate)
                    setFormWaiting(false)
                  }
                }
              },
              {
                name: 'newStartDate',
                label: t('contracts:forms.changeOwner.inputs.newStartDate'),
                type: DataType.DATE,
                required: true,
                errorState: !validations.startDate,
                errorText: t('contracts:notifications.errors.startDate'),
                onChange: (newDate, formState) => {
                  const state = formState as initialModal
                  if (
                    state?.cups &&
                    moment(newDate).isValid() &&
                    moment(state?.newEndDate).isValid()
                  ) {
                    setFormWaiting(true)
                    queryContractByCUPSDates(state.cups, moment(newDate).toDate(), state.newEndDate)
                    setFormWaiting(false)
                  }

                  validateStartDate(moment(newDate).toDate(), state?.newEndDate)
                  // validateEndDate(moment(newDate).toDate(), state?.newEndDate)
                }
              },
              {
                name: 'newEndDate',
                label: t('contracts:forms.changeOwner.inputs.newEndDate'),
                type: DataType.DATE,
                required: true,
                errorState: !validations.endDate,
                errorText: t('contracts:notifications.errors.startDate'),
                onChange: (newDate, formState) => {
                  const state = formState as initialModal
                  if (
                    state?.cups &&
                    moment(newDate).isValid() &&
                    moment(state?.newStartDate).isValid() &&
                    moment(state?.newStartDate).isSameOrBefore(newDate, 'days')
                  ) {
                    setFormWaiting(true)
                    queryContractByCUPSDates(
                      state.cups,
                      moment(state.newStartDate).toDate(),
                      moment(newDate).toDate()
                    )
                    setFormWaiting(false)
                  }

                  // validateStartDate(state?.newStartDate, moment(newDate).toDate())
                  validateEndDate(state?.newStartDate, moment(newDate).toDate())
                }
              }
            ]
          }
        ]
      }
    }, [cupsNotFound, activeContractNotFound, multipleContractsFound])

  const formSection: Section[] = useMemo(() => {
    const sections: Section[] = [
      {
        inputs: [
          {
            name: 'cups',
            label: t('contracts:forms.changeOwner.inputs.cups'),
            type: DataType.TEXT,
            required: true,
            helpText: t('contracts:forms.changeOwner.helpText.cups'),
            errorState: cupsNotFound || activeContractNotFound || multipleContractsFound,
            errorText: cupsNotFound
              ? t('contracts:forms.changeOwner.errorText.cupsNotFound')
              : activeContractNotFound
                ? t('contracts:forms.changeOwner.errorText.activeContractNotFound')
                : multipleContractsFound
                  ? 'Existen múltiples contratos activos para el período seleccionado. Seleccione un período menor.'
                  : '',
            onBlur: newCups =>
              newCups &&
              formData.newStartDate &&
              formData.newEndDate &&
              queryContractByCUPSDates(`${newCups}`, formData.newStartDate, formData.newEndDate)
          },
          {
            name: 'newStartDate',
            label: t('contracts:forms.changeOwner.inputs.newStartDate'),
            type: DataType.DATE,
            required: true,
            errorState: !validations.startDate,
            errorText: t('contracts:notifications.errors.startDate'),
            helpText: formData.newStartDate
              ? t('contracts:forms.changeOwner.helpText.newStartDate', {
                  newOldEndDate: moment(formData.newStartDate)
                    .add(-1, 'days')
                    .format(DATE_FORMATS[i18next.language] || DEFAULT_DATE_FORMAT)
                })
              : '',
            onChange: (newDate, formState) => {
              const data = formState as ChangeOwnerForm
              data.cups &&
                data.newEndDate &&
                newDate &&
                moment(newDate).isValid() &&
                moment(data?.newEndDate).isValid() &&
                moment(newDate).isSameOrBefore(data?.newEndDate, 'days') &&
                queryContractByCUPSDates(data.cups, moment(newDate).toDate(), data.newEndDate)

              validateStartDate(moment(newDate).toDate(), data?.newEndDate)
            }
          },
          {
            name: 'newEndDate',
            label: t('contracts:forms.changeOwner.inputs.newEndDate'),
            type: DataType.DATE,
            required: true,
            errorState: !validations.endDate,
            errorText: t('contracts:notifications.errors.startDate'),
            onChange: (newDate, formState) => {
              const data = formState as ChangeOwnerForm
              data.cups &&
                data.newStartDate &&
                newDate &&
                moment(newDate).isValid() &&
                moment(data.newEndDate).isValid() &&
                moment(data.newStartDate).isSameOrBefore(newDate, 'days') &&
                queryContractByCUPSDates(data.cups, data.newStartDate, moment(newDate).toDate())

              validateEndDate(data?.newStartDate, moment(newDate).toDate())
            }
          }
        ]
      },
      {
        title: t('contracts:forms.changeOwner.titles.activeContract'),
        inputs: [
          {
            name: 'activeClientDesc',
            label: t('contracts:forms.changeOwner.inputs.activeClient'),
            type: DataType.TEXT,
            required: true,
            readOnly: true
          },
          {
            name: 'activeContractDesc',
            label: t('contracts:forms.changeOwner.inputs.activeContract'),
            type: DataType.TEXT,
            required: true,
            readOnly: true
          },
          {
            name: 'activeContractStartDate',
            label: t('contracts:forms.changeOwner.inputs.startDate'),
            type: DataType.DATE,
            required: true,
            readOnly: true
          },
          {
            name: 'activeContractEndDate',
            label: t('contracts:forms.changeOwner.inputs.endDate'),
            type: DataType.DATE,
            required: true,
            readOnly: true
          }
        ]
      },
      {
        title: t('contracts:forms.changeOwner.titles.newContract'),
        inputs: [
          {
            name: 'newClientDesc',
            label: t('contracts:forms.changeOwner.inputs.newClient'),
            type: DataType.TEXT,
            required: true,
            readOnly: true
          },
          {
            name: 'signDate',
            label: t('contracts:forms.changeOwner.inputs.signDate'),
            type: DataType.DATE,
            required: true
          },
          {
            name: 'saleDate',
            label: t('contracts:forms.changeOwner.inputs.saleDate'),
            type: DataType.DATE
          },
          {
            name: 'paymentMethodId',
            label: t('contracts:forms.changeOwner.inputs.paymentMethod'),
            type: DataType.SELECT,
            selectOptions: paymentMethodOptions,
            isLoading: isFetchingPaymentOptions,
            required: true,
            onChange: newMethod => {
              handlePaymentMethodChange(+newMethod)
            }
          },
          {
            name: 'iban',
            label: t('contracts:forms.changeOwner.inputs.iban'),
            type: DataType.TEXT,
            errorState: !validations.iban,
            errorText: t('contracts:notifications.errors.iban'),
            required: ibanRequired,
            onBlur: iban => {
              handleNewIban(`${iban}`)
              // setContractDataForm(prevState => ({ ...prevState, iban: `${iban}` }))
            }
          },
          {
            name: 'activeProductId',
            label: t('contracts:forms.changeOwner.inputs.product'),
            type: DataType.SELECT,
            selectOptions: productTypeOptions,
            onChange: newProductId => {
              getQuotes(+newProductId)

              setFormData(prevState => ({
                ...prevState,
                activeProductId: formData.activeProductId,
                activeProductCode:
                  productTypeOptions?.find(option => option.value === +newProductId)?.code || null
              }))
            }
          },
          {
            name: 'activeQuoteId',
            label: t('contracts:forms.changeOwner.inputs.quote'),
            type: DataType.SELECT,
            selectOptions: quoteOptions
          },
          {
            name: 'cnaeId',
            label: t('procedures:forms.add.inputs.cnae'),
            type: DataType.SELECT,
            selectOptions: listCnaeItemMapper(cnaeList),
            required: isCnaeRequired,
            clearable: true,
            onChange: newCnaeId => {
              const cnae = cnaeList.find(cnae => cnae.id === +newCnaeId)
              setFormData(prevState => ({
                ...prevState,
                cnaeId: newCnaeId ? +newCnaeId : undefined,
                cnae: cnae?.code
              }))
            }
          }
        ]
      }
    ]

    // Only add capacity section when ADM_TEC_CHANGE
    if (showCapacitySection) {
      sections.push({
        title: t('contracts:forms.changeOwner.titles.capacity'),
        inputs: [
          {
            name: 'type',
            label: t(
              'contracts:components.capacitiesTable.actions.edit.inputs.contractCapacityType'
            ),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArray(
              EnumTipoProductoCapacidad,
              'enums:EnumTipoProductoCapacidad'
            )
          },
          {
            name: 'toll',
            label: t('contracts:components.capacitiesTable.actions.edit.inputs.toll'),
            type: DataType.SELECT,
            selectOptions: tollSelectOptionList,
            onChange: newToll => {
              setIsCnaeRequired(VALID_TOLLTYPE.includes(newToll as EnumTipoPeaje))
            }
          },
          {
            name: 'qaEstimada',
            label: t('contracts:components.capacitiesTable.actions.edit.inputs.qa'),
            suffix: t('contracts:forms.add.units.kWh'),
            type: DataType.NUMBER,
            customDecimalScale: 0
          },
          {
            name: 'qdContratada',
            label: t('contracts:components.capacitiesTable.actions.edit.inputs.qd'),
            suffix: t('contracts:forms.add.units.kWh'),
            type: DataType.NUMBER
          }
        ]
      })
    }

    if (showAtrSection) {
      sections.push({
        title: t('contracts:forms.changeOwner.titles.atr'),
        inputs: [
          {
            name: 'type',
            label: t('contracts:forms.details.inputs.ATRType'),
            type: DataType.SELECT,
            inputSize: 4,
            selectOptions: enumToSelectOptionArray(EnumTipoContratoATR, 'enums:EnumTipoContratoATR')
          },
          {
            name: 'tollType',
            label: t('contracts:forms.details.inputs.toll'),
            type: DataType.SELECT,
            required: true,
            selectOptions: tollSelectOptionList,
            onChange: (value, formState: any) => {
              onChangeToll(value)
            }
          },
          {
            name: 'period1',
            label: t('contracts:forms.add.inputs.period1'),
            suffix: t('contracts:forms.add.units.kW'),
            type: DataType.NUMBER,
            customDecimalScale: 3,
            required: periodsVersionFormConfig[1],
            display: periodsVersionFormConfig[1]
          },
          {
            name: 'period2',
            label: t('contracts:forms.add.inputs.period2'),
            suffix: t('contracts:forms.add.units.kW'),
            type: DataType.NUMBER,
            customDecimalScale: 3,
            required: periodsVersionFormConfig[2],
            display: periodsVersionFormConfig[2]
          },
          {
            name: 'period3',
            label: t('contracts:forms.add.inputs.period3'),
            suffix: t('contracts:forms.add.units.kW'),
            type: DataType.NUMBER,
            customDecimalScale: 3,
            required: periodsVersionFormConfig[3],
            display: periodsVersionFormConfig[3]
          },
          {
            name: 'period4',
            label: t('contracts:forms.add.inputs.period4'),
            suffix: t('contracts:forms.add.units.kW'),
            type: DataType.NUMBER,
            customDecimalScale: 3,
            required: periodsVersionFormConfig[4],
            display: periodsVersionFormConfig[4]
          },
          {
            name: 'period5',
            label: t('contracts:forms.add.inputs.period5'),
            suffix: t('contracts:forms.add.units.kW'),
            type: DataType.NUMBER,
            customDecimalScale: 3,
            required: periodsVersionFormConfig[5],
            display: periodsVersionFormConfig[5]
          },
          {
            name: 'period6',
            label: t('contracts:forms.add.inputs.period6'),
            suffix: t('contracts:forms.add.units.kW'),
            type: DataType.NUMBER,
            customDecimalScale: 3,
            required: periodsVersionFormConfig[6],
            display: periodsVersionFormConfig[6]
          }
        ]
      })
    }

    return sections
  }, [
    showCapacitySection,
    showAtrSection,
    quoteOptions,
    isCnaeRequired,
    formData.commodity,
    periodsVersionFormConfig
  ])

  useEffect(() => {
    updateFormDefinition(initialModalDefinition)
  }, [initialModalDefinition])

  useEffect(() => {
    getPaymentMethods()
    getActivityTypes()
  }, [])

  const getPaymentMethods = async () => {
    setIsFetchingPaymentOptions(true)
    await fetchPaymentMethodsTypes().then(response => {
      if (response.data !== undefined) {
        setPaymentMethodOptions(paymentMethodTypesToOptionsMapper(response.data))
        setPaymentMethods(response.data)
      }
      setIsFetchingPaymentOptions(false)
    })
  }

  const getActivityTypes = async () => {
    const response = await fetchActivityTypes()
    if (response.data !== undefined) {
      setCnaeList(response.data)
    }
  }

  if (!clientData) {
    history.goBack()
  }

  const onApplyCupsFilter = async ({ formData, contractData }) => {
    const commodity = enumKeyToValue(EnumCommodity, contractData.commodity)

    formData &&
      setFormData(prevState => ({
        ...prevState,
        commodity,
        cups: formData.cups,
        newClientId: clientData?.id,
        newClientDesc: getClientName(clientData),
        activeClientId: formData.activeClientId,
        activeClientDesc: formData.activeClientDesc,
        activeContractId: formData.activeContractId,
        activeProductId: formData.activeProductId,
        activeProductCode: formData.activeProductCode,
        activeQuoteId: formData.activeQuoteId,
        activeContractDesc: formData.activeContractDesc,
        activeContractStartDate: formData.activeStartDate,
        activeContractEndDate: formData.activeEndDate,
        newStartDate: moment(formData.newStartDate).toDate(),
        newEndDate: moment(formData.newEndDate).toDate(),
        signDate: moment(formData.signDate).toDate(),
        saleDate: moment(formData.saleDate).toDate(),
        changeType: formData.changeType
      }))
    contractData && setActiveContract(contractData)

    getQuotes(formData.activeProductId)
    getTolls(contractData.commodity)
    getProductTypeOptions(contractData.commodity)

    setShowCapacitySection(false)
    setShowAtrSection(false)

    if (!formData.activeContractId) return

    const contractDetailsResponse = await getContractDetails(formData.activeContractId)
    if (!contractDetailsResponse.success || !contractDetailsResponse.data) return

    const details = contractDetailsResponse.data
    const cnaeCode = details.cupsList?.[0]?.cnae
    let currentCnaeList = cnaeListRef.current
    const cnaeId = cnaeCode ? currentCnaeList.find(cnae => cnae.code === cnaeCode)?.id : undefined

    if (commodity === EnumCommodity.Electricidad) {
      setIsCnaeRequired(true)

      const isPwrAdmTecChange =
        formData.changeType === CHANGE_TYPE.ADM_TEC_CHANGE ||
        formData.changeType === CHANGE_TYPE.CHANGE_OWNER_ONLY

      const contractAtr = details.contractATR
      const lastVersion = contractAtr?.versions?.[0]
      const versionMapped = lastVersion
        ? mapATRVersionToTableModel(lastVersion, undefined)
        : undefined

      const formDataUpdate = {
        cnaeId,
        ...(isPwrAdmTecChange &&
          contractAtr &&
          versionMapped && {
            type: enumEnumToValueNumber(EnumTipoProductoCapacidad, contractAtr.type),
            tollType: versionMapped.tollType,
            period1: versionMapped.period1,
            period2: versionMapped.period2,
            period3: versionMapped.period3,
            period4: versionMapped.period4,
            period5: versionMapped.period5,
            period6: versionMapped.period6
          })
      }

      setFormData(prevState => ({
        ...prevState,
        ...formDataUpdate
      }))

      if (isPwrAdmTecChange && versionMapped) {
        onChangeToll(versionMapped.tollType)
        setShowAtrSection(true)
      }
    } else {
      const capacity = details.capacities?.[0]

      const isGasAdmTecChange =
        formData.changeType === CHANGE_TYPE.ADM_TEC_CHANGE ||
        formData.changeType === CHANGE_TYPE.CHANGE_OWNER_ONLY

      if (capacity) {
        const tollValue = enumKeyToValue(EnumTipoPeaje, capacity.toll) as EnumTipoPeaje
        setIsCnaeRequired(VALID_TOLLTYPE.includes(tollValue))
      }

      const formDataUpdate = {
        cnaeId,
        ...(capacity &&
          isGasAdmTecChange && {
            type: enumEnumToValueNumber(EnumTipoProductoCapacidad, capacity.type),
            toll: enumEnumToValueNumber(EnumTipoPeaje, capacity.toll),
            qaEstimada: capacity.qaEstimada,
            qdContratada: capacity.qdContratada,
            contractType: enumEnumToValueNumber(EnumServicio, capacity.contractType),
            state: capacity.status,
            cnae: cnaeCode
          })
      }

      setFormData(prevState => ({
        ...prevState,
        ...formDataUpdate
      }))

      if (capacity && isGasAdmTecChange) {
        setShowCapacitySection(true)
      }
    }
  }

  return (
    <Page
      notification={globalNotification}
      fullWidth={false}
      layout={PageLayout.SINGLE_COLUMN}
      error={errorProps}
    >
      <PageHeader title={t('contracts:titleChangeOwner')}>
        <Button
          variant="text"
          color="primary"
          size="small"
          disabled={isSaving}
          onClick={() => {
            history.goBack()
          }}
        >
          {t('contracts:buttonsAdd.cancel')}
        </Button>
        <ButtonWithPermissions
          variant="contained"
          color="primary"
          size="small"
          disabled={isSaving || validations.isDirty}
          // disabled={isFetching || isSaving || validations.isDirty || !pendingChanges}
          hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          onClick={onSave}
        >
          {t('contracts:buttonsAdd.save')}
        </ButtonWithPermissions>
      </PageHeader>
      <Box mb={1}>
        <Breadcrumbs separator=">" aria-label="breadcrumb">
          {clientData && (
            <BreadcrumbLink
              toPathName="/client/file"
              state={{ clientID: clientData?.id }}
              confirmation={pendingChanges}
            >
              {`Cliente ${getLinkClientText(clientData)} - ID ${clientData.documentID}`}
            </BreadcrumbLink>
          )}
          <Typography key="3" color="primary" variant="subtitle1">
            {t('contracts:breadcrumbs.changeOwner')}
          </Typography>
        </Breadcrumbs>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper elevation={6}>
            {!isFetching ? (
              <Form
                title={t('contracts:forms.changeOwner.titles.supply')}
                sections={formSection}
                data={formData}
                options={formOptions}
              />
            ) : (
              <FormSkeleton columns={3} inputs={6} elevation={2} withTitle={true} />
            )}
          </Paper>
        </Grid>
      </Grid>
      <CupsFilter onApplyFilter={onApplyCupsFilter} />
    </Page>
  )
}
